import { useTranslation } from "react-i18next";
import { UploadOutlined } from "@ant-design/icons";
import { Discussion } from "./CommentFeed";
import { MenuOption } from "./CommentSection";
import { CommentNode } from "./commentTree";

export interface Attachment {
  id: number | null;
  commentId?: number | null;
  mainKey: string;
  fileName: string;
  attachmentType: "image" | "file";
}

export interface Author {
  id: number;
  fullName: string;
  avatar: string | null;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface ReactionCount {
  reactionType: number;
  count: number;
}

export interface RawComment {
  id: number;
  content: string;
  createdDate: string;
  parentId: number | null;
  level: number;
  author: Author;
  myReaction: number | null;
  reactionCount: ReactionCount[];
  attachments: Attachment[];
  discussionId: number;
  avatar: string | null;
  isMine: boolean;
  isReport?: boolean | null;
  isSave?: boolean;
  isPinned?: boolean;
  totalReply: number;
  commenterUserId?: number;
}
export interface ApiResponse<T> {
  data: {
    data: T;
    message?: string;
    status?: number;
  };
}
export interface CourseManagementService {
  teacherReplyStudent: (payload: FormData) => Promise<ApiResponse<RawComment>>;
  updateTeacherReplyStudent: (
    payload: FormData
  ) => Promise<ApiResponse<RawComment>>;
  deleteTeacherReplyStudent: (commentId: number) => Promise<void>;
  discussionReact: (data: {
    id: number;
    reactionType: number;
  }) => Promise<void>;
}
export interface CommentFeedProps {
  data: Discussion[];
  getMenuOptions?: (comment: CommentNode) => MenuOption[];
  onEditComment?: (
    commentId: number,
    content: string,
    attachments?: any
  ) => Promise<RawComment>;
  onDeleteComment?: (commentId: number) => Promise<void>;
  onSubmitReply?: (data: {
    discussionId: number;
    parentId: number;
    replyToId: number;
    level: number;
    content: string;
    attachments?: File[];
  }) => Promise<RawComment>;
  onLikeComment?: (commentId: number) => Promise<void>;
  onDislikeComment?: (commentId: number) => Promise<void>;
  onDataUpdate?: (updatedData: Discussion[]) => void;
  className?: string;
}

export const getFileIcon = (fileType: string) => {

  switch (fileType) {
    case "application/pdf":
    case "pdf":
      return (
        <img
          src={"/images/pdf-icon.svg"}
          alt="file"
          style={{ width: "48px", height: "48px" }}
        />
      );
    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "word":
      return (
        <img
          src={"/images/word-icon.svg"}
          alt="file"
          style={{ width: "48px", height: "48px" }}
        />
      );
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
      return (
        <img
          src={"/images/pptx-icon.svg"}
          alt="file"
          style={{ width: "48px", height: "48px" }}
        />
      );
    case "application/vnd.ms-excel":
    case "ms-excel":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return (
        <img
          src={"/images/xlsx-icon.svg"}
          alt="file"
          style={{ width: "48px", height: "48px" }}
        />
      );
    case "text/plain":
    case "text":
      return (
        <img
          src={"/images/txt-icon.svg"}
          alt="file"
          style={{ width: "48px", height: "48px" }}
        />
      );
    case "image/jpeg":
    case "image/png":
    case "image/gif":
      return (
        <img
          src={URL.createObjectURL(new Blob([fileType], { type: fileType }))}
          alt="file"
          style={{ width: "48px", height: "48px" }}
        />
      );
    case "video":
      return (
        <img
          src={"/images/mp4-icon.svg"}
          alt="file"
          style={{ width: "48px", height: "48px" }}
        />
      );
    case "audio":
    case "mp3":
      return (
        <img
          src={"/images/mp3-icon.svg"}
          alt="file"
          style={{ width: "48px", height: "48px" }}
        />
      );
    default:
      return (
        <UploadOutlined style={{ fontSize: "24px", color: "#8c8c8c" }} />
      );
  }
};

export const isImageFile = (fileName: string): boolean => {
  const imageExtensions = [
    ".jpg",
    ".jpeg",
    ".png",
    ".gif",
    ".webp",
    ".bmp",
    ".svg",
    ".ico",
  ];
  return imageExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
};

export const getBase64 = (file: any): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

export const validateFileType = (
  fileName: string,
  acceptedTypes: string[]
): boolean => {
  const ext = fileName.split(".").pop()?.toLowerCase();
  return acceptedTypes.some((type) => {
    if (type.startsWith(".")) {
      return `.${ext}` === type.toLowerCase();
    }
    if (type.includes("/")) {
      const [mainType] = type.split("/");
      if (mainType === "image") {
        return isImageFile(fileName);
      }
    }
    return false;
  });
};
